import { useCallback, useEffect, useState } from 'react';
import { purchasingAPI, ContractPriceChangeLog, ContractPriceDerivedCreated } from '../../lib/api';

/** DRF's global PAGE_SIZE — the Contract Logs page size. */
export const CONTRACT_LOGS_PAGE_SIZE = 20;

interface ContractChangelogState {
  logs: ContractPriceChangeLog[];
  /** The SERVER's trail total (CR-6424 R2), not a page length. */
  count: number;
  /** "Created by <user> on <date>" when the trail has no creation row. */
  derivedCreated: ContractPriceDerivedCreated | null;
  page: number;
  setPage: (page: number) => void;
  /** Re-fetch the current page (after an inline save). */
  refresh: () => void;
}

/**
 * Contract Logs for the Contract Details pop-up, one server page at a time.
 *
 * Extracted from ContractPriceDetailModal (500-line cap) for the Customer review
 * 11 Sep (review item 21): the response now also carries `derived_created`.
 *
 * @param contractId - contract whose trail to load
 * @param isOpen - only fetch while the pop-up is open AND the viewer may read
 *   the trail (CR-6346: suppliers are refused it, so the caller passes false)
 * @param refreshTick - bumped by the parent after a save in the edit modal
 * @returns the page of logs, the server count, the derived first entry and paging
 * @link ContractPriceHistoryTab renders it
 */
export function useContractChangelog(
  contractId: number,
  isOpen: boolean,
  refreshTick: number,
): ContractChangelogState {
  const [logs, setLogs] = useState<ContractPriceChangeLog[]>([]);
  const [count, setCount] = useState(0);
  const [derivedCreated, setDerivedCreated] = useState<ContractPriceDerivedCreated | null>(null);
  const [page, setPage] = useState(1);

  // Reset the trail to page 1 when the modal moves to another contract.
  useEffect(() => {
    setPage(1);
  }, [contractId]);

  const fetchPage = useCallback(async (target: number) => {
    try {
      const response = await purchasingAPI.getContractPriceChangelog(
        contractId,
        { page: target, page_size: CONTRACT_LOGS_PAGE_SIZE },
      );
      setLogs(response.data.results);
      setCount(response.data.count);
      setDerivedCreated(response.data.derived_created ?? null);
    } catch (err: unknown) {
      console.error('Failed to load contract changelog:', err);
      // DRF 404s an out-of-range page (a shrunken trail can strand us there)
      // — fall back to page 1 rather than erroring on data that exists.
      if (target > 1) {
        setPage(1);
      } else {
        setLogs([]);
        setCount(0);
        setDerivedCreated(null);
      }
    }
  }, [contractId]);

  useEffect(() => {
    if (isOpen && contractId) {
      fetchPage(page);
    }
  }, [isOpen, contractId, refreshTick, page, fetchPage]);

  const refresh = useCallback(() => { fetchPage(page); }, [fetchPage, page]);

  return { logs, count, derivedCreated, page, setPage, refresh };
}
